import { type Breadcrumb, type Extras, type SeverityLevel } from '@sentry/core';
// This module is the sanctioned wrapper around manual Sentry capture, hence the allowed direct import.
// oxlint-disable-next-line no-restricted-imports
import { addBreadcrumb, captureException, captureMessage } from '@sentry/react';
import { type SentryDomain, type SentryTags, type SentryVisibility } from '@/api/errors';

/**
 * Options shared by {@link reportError} and {@link reportMessage}.
 *
 * Dynamic values (ids, messages, payloads) belong in {@link ReportOptions.extra}, never interpolated
 * into the error message — keeping messages static is what lets Sentry group events into one issue
 * per failure instead of one per account/session.
 */
export type ReportOptions = {
  /** Feature area that owns this error. Required, to force the ownership question at the call site. */
  domain: SentryDomain;
  /** What was being attempted, e.g. `'stripe-setup'`. Tagged for filtering. */
  action?: string;
  /** Severity per the taxonomy. Defaults to `'error'`. */
  level?: SeverityLevel;
  /** Whether — and how — the user saw this. */
  visibility?: SentryVisibility;
  /** Dynamic, searchable context. Keep ids/payloads here, out of the message. */
  extra?: Extras;
  /** Escape hatch for additional taxonomy tags beyond the ones above. */
  tags?: SentryTags;
  /**
   * Explicit issue grouping key, overriding Sentry's stack-based default. Needed when distinct
   * failures share one call site: without it they collapse into a single issue.
   */
  fingerprint?: string[];
};

/**
 * Options for {@link reportBreadcrumb}.
 */
export type BreadcrumbOptions = {
  /** Feature area this milestone belongs to. Becomes the breadcrumb category. */
  domain: SentryDomain;
  /** Severity per the taxonomy. Defaults to `'info'`. */
  level?: SeverityLevel;
  /** Dynamic context for this step. Keep ids/durations/payloads here, out of the message. */
  data?: Breadcrumb['data'];
};

function toCaptureContext({ domain, action, level = 'error', visibility, extra, tags, fingerprint }: ReportOptions) {
  const mergedTags: SentryTags = { ...tags, domain, surface: 'manual' };
  if (action !== undefined) {
    mergedTags.action = action;
  }
  if (visibility !== undefined) {
    mergedTags.visibility = visibility;
  }
  return { level, tags: mergedTags, extra, fingerprint };
}

/**
 * Coerces an arbitrary thrown value into an `Error` so Sentry always has a stack trace and groups
 * by stack rather than by a stringified message (which may carry dynamic content).
 */
// A thrown value is the one input that genuinely has no type to parse at its boundary: this
// function is that boundary, and narrowing it is all it does.
// oxlint-disable-next-line eslint-js/no-restricted-syntax
function toError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'string') {
    return new Error(value);
  }
  return new Error('Non-error value reported to Sentry', { cause: value });
}

/**
 * The sanctioned way to capture an error outside the automatic QueryClient pipeline. Enforces the
 * canonical tag taxonomy (`domain`, `surface: 'manual'`, optional `action`/`visibility`) so every
 * manual report is dashboard-ready and consistently grouped.
 *
 * @example
 * reportError(error, { domain: 'billing', action: 'stripe-setup', extra: { accountId, sessionId } });
 */
export function reportError(error: unknown, options: ReportOptions): void {
  captureException(toError(error), toCaptureContext(options));
}

/**
 * Like {@link reportError} but for a static, code-defined message rather than a thrown value. Use it
 * to flag an invalid-but-recoverable state (e.g. malformed remote config). Keep the message constant
 * so it groups into a single issue; put the specifics in {@link ReportOptions.extra}.
 *
 * @example
 * reportMessage('Invalid experiment config', { domain: 'flags', level: 'warning', extra: { key, issues } });
 */
export function reportMessage(message: string, options: ReportOptions): void {
  captureMessage(message, toCaptureContext(options));
}

/**
 * Records a step in the trail that Sentry attaches to whatever error is reported next. Costs nothing
 * until an event is actually sent, which is what makes it the right tool for lifecycle milestones
 * (a token refreshed, a session ended) that are worth seeing *around* a failure but are not failures
 * themselves and must not become issues of their own.
 *
 * Keep {@link BreadcrumbOptions.data} for the dynamic values, as with {@link ReportOptions.extra}.
 *
 * @example
 * reportBreadcrumb('Access token refreshed', { domain: 'auth', data: { expiresIn, durationMs } });
 */
export function reportBreadcrumb(message: string, { domain, level = 'info', data }: BreadcrumbOptions): void {
  addBreadcrumb({ category: domain, message, level, data });
}

/**
 * The readable message of every issue in a zod error, for {@link ReportOptions.extra}.
 *
 * Not `error.message`: since zod 4 that is a JSON dump of the issue objects, far too noisy to attach
 * to a report. Structurally typed so this module keeps no dependency on zod.
 */
export const zodIssueMessages = ({ issues }: { issues: readonly { message: string }[] }): string[] =>
  issues.map((issue) => issue.message);
